import { sanitizeCardDescription } from '@/utils/sanitize'
import { functionType } from '@/composables/useWorkflowAssistantFunctions'

const findCardById = (svgGraph, cardId) => {
  for (const eventId of Object.keys(svgGraph)) {
    const cards = svgGraph[eventId]?.cards || []
    const card = cards.find((c) => c.id === cardId)
    if (card) {
      return card
    }
  }

  return null
}

const refToName = (ref) => typeof ref === 'string'
  ? ref.split('/').pop().replace(/([a-z])([A-Z])/g, '$1 $2')
  : ''

const describeEventCardArgs = (args) => {
  const parts = []
  if (args.aggregateRoot !== undefined) {
    parts.push(args.aggregateRoot
      ? `set the aggregate root card to ${refToName(args.aggregateRoot)}`
      : 'removed the aggregate root card')
  }
  if (args.acceptanceCriteria !== undefined) {
    parts.push(args.acceptanceCriteria.length > 0
      ? `set ${args.acceptanceCriteria.length} Given-When-Then card${args.acceptanceCriteria.length === 1 ? '' : 's'}`
      : 'removed the Given-When-Then cards')
  }

  return parts.length > 0 ? ` (${parts.join(', ')})` : ''
}

export const getUpdateSummary = (type, args, { svgGraph, cardTypes }, result) => {
  const event = args.eventId ? svgGraph[args.eventId] : null
  const domainEventName = typeof args.domainEvent === 'string'
    ? args.domainEvent.split('/').pop().replace(/([a-z])([A-Z])/g, '$1 $2')
    : ''
  const cardType = args.cardTypeId ? cardTypes.find((item) => item.id === args.cardTypeId) : null
  const card = args.cardId ? findCardById(svgGraph, args.cardId) : null

  switch (type) {
    case functionType.UPDATE_WORKFLOW_NAME:
      return `Changed workflow name to "${args.name}"`
    case functionType.CREATE_EVENT: {
      const shape = args.type === 'decision' ? 'decision' : 'event'

      return `Created new ${shape} "${args.description}"${describeEventCardArgs(args)}`
    }
    case functionType.UPDATE_EVENT: {
      const shape = args.domainEvent?.includes('/decisions/') ? 'decision' : 'event'
      const base = args.description
        ? `Updated ${shape} description to "${args.description}"`
        : `Updated ${shape} "${domainEventName}"`

      return `${base}${describeEventCardArgs(args)}`
    }
    case functionType.DELETE_EVENT:
      return `Deleted ${args.domainEvent?.includes('/decisions/') ? 'decision' : 'event'} "${domainEventName}"`
    case functionType.ADD_CONNECTION:
      return 'Added a connection'
    case functionType.REMOVE_CONNECTION:
      return 'Removed a connection'
    case functionType.CREATE_LANE:
      return `Created new lane "${args.name}"`
    case functionType.UPDATE_LANE:
      if (args.position !== undefined && args.name === undefined) {
        return `Moved lane "${args.lane}" to position ${args.position + 1}`
      }

      return args.position !== undefined
        ? `Updated lane "${args.lane}"`
        : `Renamed lane "${args.lane}" to "${args.name}"`
    case functionType.DELETE_LANE:
      return `Deleted lane "${args.lane ?? ''}"`
    case functionType.CREATE_GROUP:
      return `Created new group "${args.name}"`
    case functionType.UPDATE_GROUP:
      return `Renamed group "${args.group}" to "${args.name}"`
    case functionType.DELETE_GROUP:
      return `Deleted group "${args.group ?? ''}"`
    case functionType.CREATE_CARD:
      return `Created new ${cardType?.title ?? ''} card on "${event?.name ?? domainEventName}"`
    case functionType.DELETE_CARD:
      return `Deleted card "${sanitizeCardDescription(args.description ?? card?.description ?? '')}"`
    case functionType.DETACH_SCHEMAS:
      return result.message
    case functionType.UPDATE_CARD: {
      const updates = []
      if (args.description !== undefined) {
        updates.push('description')
      }
      if (args.cardTypeId !== undefined) {
        updates.push(`type to "${cardType?.title ?? args.cardTypeId}"`)
      }
      if (args.releaseNo !== undefined) {
        updates.push(args.releaseNo ? `release ${args.releaseNo}` : 'cleared release')
      }
      if (args.done !== undefined) {
        updates.push(args.done ? 'marked done' : 'marked not done')
      }
      if (args.schema !== undefined) {
        updates.push(args.schema ? 'linked schema' : 'unlinked schema')
      }

      return `Updated card: ${updates.join(', ') || 'no changes'}`
    }
    case functionType.CREATE_ENTITY: {
      const entities = args.entities || []

      if (entities.length === 0) {
        return 'No entities created'
      }

      const descriptions = entities.map((item) => {
        const fieldNames = (item.fields || []).map((field) => field.name).filter(Boolean)
        const extras = []
        if (item.boundedContext) {
          extras.push(`in the ${item.boundedContext} bounded context`)
        }
        if ((item.aggregateRootFor || []).length > 0) {
          extras.push(`as aggregate root of ${item.aggregateRootFor.map(refToName).join(', ')}`)
        }
        const placement = extras.length > 0 ? `, ${extras.join(', ')}` : ''
        if (fieldNames.length === 0) {
          return `"${item.name}"${placement}`
        }
        const withExamples = (item.fields || []).some((field) => (field.exampleData || []).length > 0)

        return `"${item.name}" with fields ${fieldNames.join(', ')}${withExamples ? ' (including example data)' : ''}${placement}`
      })

      return descriptions.length === 1
        ? `Created new entity ${descriptions[0]}`
        : `Created ${descriptions.length} entities: ${descriptions.join('; ')}`
    }
    case functionType.UPDATE_ENTITY: {
      const entities = args.entities || []

      if (entities.length === 0) {
        return 'No entities updated'
      }

      const descriptions = entities.map((item) => {
        const changes = []
        if (item.removeFields?.length) {
          changes.push(`removed ${item.removeFields.join(', ')}`)
        }
        if (item.updateFields?.length) {
          changes.push(`updated ${item.updateFields.map((update) => update.fieldName).filter(Boolean).join(', ')}`)
        }
        if (item.addFields?.length) {
          changes.push(`added ${item.addFields.map((field) => field.name).filter(Boolean).join(', ')}`)
        }
        if (item.name !== undefined) {
          changes.push(`renamed to "${item.name}"`)
        }
        if (item.description !== undefined) {
          changes.push(item.description ? 'updated description' : 'removed description')
        }
        const label = refToName(item.entity)

        return changes.length > 0 ? `"${label}": ${changes.join(', ')}` : `"${label}"`
      })

      return descriptions.length === 1
        ? `Updated entity ${descriptions[0]}`
        : `Updated ${descriptions.length} entities — ${descriptions.join(' | ')}`
    }
    case functionType.DELETE_ENTITY:
      return `Deleted entity "${refToName(args.entity)}"`
    case functionType.CREATE_COMMAND: {
      const commands = args.commands || []

      if (commands.length === 0) {
        return 'No commands created'
      }

      const descriptions = commands.map((item) => {
        const fieldNames = (item.fields || []).map((field) => field.name).filter(Boolean)

        return fieldNames.length === 0
          ? `"${item.name}"`
          : `"${item.name}" with fields ${fieldNames.join(', ')}`
      })

      return descriptions.length === 1
        ? `Created command ${descriptions[0]}`
        : `Created ${descriptions.length} commands: ${descriptions.join('; ')}`
    }
    case functionType.UPDATE_COMMAND: {
      const commands = args.commands || []

      if (commands.length === 0) {
        return 'No commands updated'
      }

      const descriptions = commands.map((item) => {
        const changes = []
        if (item.removeFields?.length) {
          changes.push(`removed ${item.removeFields.join(', ')}`)
        }
        if (item.updateFields?.length) {
          changes.push(`updated ${item.updateFields.map((update) => update.fieldName).filter(Boolean).join(', ')}`)
        }
        if (item.addFields?.length) {
          changes.push(`added ${item.addFields.map((field) => field.name).filter(Boolean).join(', ')}`)
        }
        if (item.name !== undefined) {
          changes.push(`renamed to "${item.name}"`)
        }
        const label = refToName(item.command)

        return changes.length > 0 ? `"${label}": ${changes.join(', ')}` : `"${label}"`
      })

      return descriptions.length === 1
        ? `Updated command ${descriptions[0]}`
        : `Updated ${descriptions.length} commands — ${descriptions.join(' | ')}`
    }
    case functionType.DELETE_COMMAND:
      return `Removed the command card "${refToName(args.command)}"`
    case functionType.CREATE_READ_MODEL: {
      const readModels = args.readModels || []

      if (readModels.length === 0) {
        return 'No read models created'
      }

      const descriptions = readModels.map((item) => {
        const fieldNames = (item.fields || []).map((field) => field.name).filter(Boolean)

        return fieldNames.length === 0
          ? `"${item.name}"`
          : `"${item.name}" with fields ${fieldNames.join(', ')}`
      })

      return descriptions.length === 1
        ? `Created read model ${descriptions[0]}`
        : `Created ${descriptions.length} read models: ${descriptions.join('; ')}`
    }
    case functionType.UPDATE_READ_MODEL: {
      const readModels = args.readModels || []

      if (readModels.length === 0) {
        return 'No read models updated'
      }

      const descriptions = readModels.map((item) => {
        const changes = []
        if (item.removeFields?.length) {
          changes.push(`removed ${item.removeFields.join(', ')}`)
        }
        if (item.updateFields?.length) {
          changes.push(`updated ${item.updateFields.map((update) => update.fieldName).filter(Boolean).join(', ')}`)
        }
        if (item.addFields?.length) {
          changes.push(`added ${item.addFields.map((field) => field.name).filter(Boolean).join(', ')}`)
        }
        if (item.name !== undefined) {
          changes.push(`renamed to "${item.name}"`)
        }
        if (item.description !== undefined) {
          changes.push(item.description ? 'updated description' : 'removed description')
        }
        if (item.cardinality !== undefined) {
          changes.push(`cardinality ${item.cardinality}`)
        }
        if (item.entity !== undefined) {
          changes.push('updated entity')
        }
        const label = refToName(item.readModel)

        return changes.length > 0 ? `"${label}": ${changes.join(', ')}` : `"${label}"`
      })

      return descriptions.length === 1
        ? `Updated read model ${descriptions[0]}`
        : `Updated ${descriptions.length} read models — ${descriptions.join(' | ')}`
    }
    case functionType.DELETE_READ_MODEL:
      return `Removed the read model card "${refToName(args.readModel)}"`
    case functionType.CREATE_BOUNDED_CONTEXT:
      return `Created new bounded context "${args.name}"`
    case functionType.UPDATE_BOUNDED_CONTEXT:
      return `Renamed bounded context "${args.boundedContext}" to "${args.name}"`
    case functionType.DELETE_BOUNDED_CONTEXT:
      return `Deleted bounded context "${args.boundedContext ?? ''}"`
    case functionType.CREATE_DOMAIN_EVENT_SCHEMA: {
      const schemas = args.domainEventSchemas || []

      if (schemas.length === 0) {
        return 'No domain event schemas created'
      }

      const descriptions = schemas.map((item) => {
        const fieldNames = (item.fields || []).map((field) => field.name).filter(Boolean)

        return fieldNames.length === 0
          ? `"${item.name}"`
          : `"${item.name}" with fields ${fieldNames.join(', ')}`
      })

      return descriptions.length === 1
        ? `Created domain event schema ${descriptions[0]}`
        : `Created ${descriptions.length} domain event schemas: ${descriptions.join('; ')}`
    }
    case functionType.UPDATE_DOMAIN_EVENT_SCHEMA: {
      const schemas = args.domainEventSchemas || []

      if (schemas.length === 0) {
        return 'No domain event schemas updated'
      }

      const descriptions = schemas.map((item) => {
        const changes = []
        if (item.removeFields?.length) {
          changes.push(`removed ${item.removeFields.join(', ')}`)
        }
        if (item.updateFields?.length) {
          changes.push(`updated ${item.updateFields.map((update) => update.fieldName).filter(Boolean).join(', ')}`)
        }
        if (item.addFields?.length) {
          changes.push(`added ${item.addFields.map((field) => field.name).filter(Boolean).join(', ')}`)
        }
        if (item.name !== undefined) {
          changes.push(`renamed to "${item.name}"`)
        }
        if (item.entity !== undefined) {
          changes.push('updated entity')
        }
        const label = refToName(item.domainEventSchema)

        return changes.length > 0 ? `"${label}": ${changes.join(', ')}` : `"${label}"`
      })

      return descriptions.length === 1
        ? `Updated domain event schema ${descriptions[0]}`
        : `Updated ${descriptions.length} domain event schemas — ${descriptions.join(' | ')}`
    }
    case functionType.DELETE_DOMAIN_EVENT_SCHEMA:
      return `Deleted domain event schema "${refToName(args.domainEventSchema)}" and its card`
    case functionType.REGENERATE_CARDS:
      if (result?.error) {
        return result.error
      }

      if (result?.created === 0) {
        return 'No cards were regenerated'
      }

      return `Regenerated ${result?.created ?? 0} cards (deleted ${result?.deleted ?? 0} old cards)`
    case functionType.ADD_MISSING_CARDS:
      if (result?.error) {
        return result.error
      }

      if (result?.created === 0) {
        return 'No missing cards found - all events already have cards for enabled card types'
      }

      return `Added ${result?.created ?? 0} missing cards`
    case functionType.REGENERATE_COMMANDS:
      if (result?.created === 0) {
        return 'No commands were regenerated'
      }

      return `Regenerated commands on ${result?.created ?? 0} events (deleted ${result?.deleted ?? 0} old)`
    case functionType.ADD_MISSING_COMMANDS:
      if (result?.created === 0) {
        return 'No missing commands found - all Command cards already have fields defined'
      }

      return `Added commands to ${result.created} events`
    case functionType.REGENERATE_READ_MODELS:
      if (result?.created === 0) {
        return 'No read models were regenerated'
      }

      return `Regenerated read models on ${result?.created ?? 0} events (deleted ${result?.deleted ?? 0} old)`
    case functionType.ADD_MISSING_READ_MODELS:
      if (result?.created === 0) {
        return 'No missing read models found - all ReadModel cards already have fields defined'
      }

      return `Added read models to ${result.created} events`
    case functionType.REGENERATE_ENTITIES:
      if (result?.error) {
        return result.error
      }

      if (result?.created === 0) {
        return 'No entities were regenerated'
      }

      return `Regenerated ${result?.created ?? 0} entities (deleted ${result?.deleted ?? 0} old entities)`
    case functionType.ADD_MISSING_ENTITIES:
      if (result?.error) {
        return result.error
      }

      if (result?.created === 0) {
        return result?.skipped > 0
          ? `All ${result.skipped} suggested entities already exist`
          : 'No missing entities found'
      }

      return result?.skipped > 0
        ? `Added ${result.created} entities (${result.skipped} already existed)`
        : `Added ${result.created} entities`
    case functionType.GENERATE_ENTITIES_FROM_DESCRIPTION:
      if (result?.deleted !== undefined) {
        return `Generated ${result.created} entities from description (deleted ${result.deleted} old entities)`
      }

      if (result?.created === 0) {
        return result?.skipped > 0
          ? `All ${result.skipped} entities from description already exist`
          : 'No entities were generated from description'
      }

      return result?.skipped > 0
        ? `Generated ${result.created} entities from description (${result.skipped} already existed)`
        : `Generated ${result.created} entities from description`
    case functionType.GENERATE_COMMANDS_FROM_DESCRIPTION:
      if (result?.deleted !== undefined) {
        return `Generated commands on ${result.created} events from description (deleted ${result.deleted} old)`
      }

      if (result?.created === 0) {
        return 'No commands were generated from description'
      }

      return `Generated commands on ${result.created} events from description`
    case functionType.GENERATE_READ_MODELS_FROM_DESCRIPTION:
      if (result?.deleted !== undefined) {
        return `Generated read models on ${result.created} events from description (deleted ${result.deleted} old)`
      }

      if (result?.created === 0) {
        return 'No read models were generated from description'
      }

      return `Generated read models on ${result.created} events from description`
    case functionType.GENERATE_CARDS_FROM_DESCRIPTION:
      if (result?.error) {
        return result.error
      }

      if (result?.deleted !== undefined) {
        return `Generated ${result.created} cards from description (replaced ${result.deleted} existing cards)`
      }

      if (result?.created === 0) {
        return 'No cards were generated - all events already have cards for enabled card types'
      }

      return `Generated ${result.created} cards from description`
    case functionType.PLAN_WORKFLOW_WITH_AI:
      if (result?.error) {
        return result.error
      }

      if (!result?.events?.length) {
        return 'No events were planned from the description'
      }

      const eventsList = result.events
        .map((e, i) => `${i + 1}. ${e.role}: "${e.domainEvent}"`)
        .join('\n')

      return `Planned ${result.events.length} events:\n${eventsList}`
    case functionType.VALIDATE_DOMAIN_MODEL:
      if (result?.isValid) {
        return 'Domain model validation passed - no issues found.'
      }

      const validationIssues = result?.issues || []
      const issueLines = validationIssues.map((issue) => `- ${issue.message}`)

      return `Found ${validationIssues.length} issue${validationIssues.length === 1 ? '' : 's'}:\n${issueLines.join('\n')}`
    case functionType.ANALYZE_DOMAIN_MODEL:
      if (result?.error) {
        return result.error
      }

      // `result.summary` is a stats object, not text; the model reads it from the tool result.
      return 'Analysed the domain model'
    case functionType.ROLLBACK_WORKFLOW:
      if (result?.error) {
        return result.error
      }

      return result?.message || 'Workflow rollback completed.'
    default:
      return ''
  }
}

// A prefix with no quote in it, followed by the quoted subject — "Renamed lane "A" to "B""
// deliberately fails to match, since its prefix carries a quote.
const NAMED = /^([^"]*?)\s+"([^"]+)"$/

// Consecutive calls to the same tool collapse into one line; a 12-round turn would
// otherwise repeat the same sentence a dozen times.
const groupUpdates = (updates) => {
  const groups = []

  for (const { name, text } of updates) {
    const match = text.match(NAMED)
    const last = groups[groups.length - 1]

    if (match && last && last.tool === name && last.prefix === match[1]) {
      last.names.push(match[2])
      continue
    }

    groups.push({ tool: name, prefix: match ? match[1] : null, names: match ? [match[2]] : [], text })
  }

  return groups.map((group) => {
    if (!group.prefix || group.names.length === 1) {
      return group.text
    }

    const words = group.prefix.replace(/\s+new\b/, '').split(' ')
    const noun = words.pop()

    return `${words.join(' ')} ${group.names.length} ${noun}s: ${group.names.map((item) => `"${item}"`).join(', ')}`
  })
}

export const getUpdatesSummaryText = (updates = []) => {
  const lines = groupUpdates(updates)

  if (lines.length === 0) {
    return 'What else can I help you with?'
  }

  const body = lines.length > 1
    ? `${lines.map((line) => `- ${line}`).join('\n')}\n`
    : `${lines[0]}.\n`

  return `${body}\nWhat else can I help you with?`
}
